import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collectSidebarPathLabels,
  getSidebarItems,
} from '../../components/Layout/config/SidebarItems';
import Badge from '../../components/ui/Badge';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PlusIcon,
  TableIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { CopyIcon, PencilIcon, TrashIcon, ChartTrendingUpIcon } from '../../components/ui/Icons';
import { formatDate } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { cn } from '../../lib/utils';
import { useModuleEntitlements } from '../admin/module-management';
import {
  describeSidebarPlacements,
  sidebarPlacementsFromSchema,
} from './sidebarPlacements';
import {
  cloneEFormDefinition,
  createEFormDefinition,
  deleteEFormDefinition,
} from '../../services/mutations';
import type { ListQueryOptions } from '../../services/paginatedList';
import {
  EFormDefinition,
  canDeleteEFormDefinition,
  getDraftVersion,
  getPublishedVersion,
  useEFormDefinitions,
  usePublishedEForms,
} from './useData';

type EnabledTab = 'active' | 'inactive' | 'all';

const enabledTabs: Array<{ id: EnabledTab; label: string; icon: React.ReactNode }> = [
  { id: 'active', label: 'Enabled', icon: <CheckCircleIcon className="h-4 w-4" /> },
  { id: 'inactive', label: 'Disabled', icon: <FolderIcon className="h-4 w-4" /> },
  { id: 'all', label: 'All', icon: <DocumentTextIcon className="h-4 w-4" /> },
];

/** Published forms catalog — start filling. */
export const CatalogList: React.FC = () => {
  const { can } = useAuth();
  const { colors } = useTheme();
  const canFill = can('e_form_submissions', 'edit') || can('e_form_submissions', 'view');
  const canManage = can('e_form_definitions', 'edit');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<EnabledTab>('active');
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'name', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc',
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction, canManage]);

  const listOptions = useMemo((): ListQueryOptions => {
    const filters: Record<string, string[]> = {};
    if (canManage) {
      if (tab === 'active') filters.isActive = ['Yes'];
      if (tab === 'inactive') filters.isActive = ['No'];
    } else {
      filters.isActive = ['Yes'];
    }
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: Object.keys(filters).length ? { filters } : undefined,
    };
  }, [canManage, tab, sortConfig.key, sortConfig.direction]);

  const { data, total, isLoading } = usePublishedEForms(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || (canManage && tab !== 'all'));
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    if (canManage) setTab('all');
  };

  return (
    <GeneralList
      title={canManage ? undefined : 'E-Forms'}
      tabs={canManage ? enabledTabs.map(({ id, label, icon }) => ({ id, label, icon })) : undefined}
      activeTab={canManage ? tab : undefined}
      onTabChange={canManage ? setTab : undefined}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search forms by name…',
      }}
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading forms…"
      isEmpty={!isLoading && data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? 'No forms match your search' : 'No published forms yet',
        hint: hasFilters
          ? 'Try adjusting filters or clearing the search.'
          : 'Ask an admin to publish a form in the Form builder.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            {canFill ? (
              <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
            ) : null}
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap">
              Name
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
              Published
            </th>
            <GeneralListSortableTh sortKey="isActive" className="whitespace-nowrap">
              Enabled
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
              Records
            </th>
            <th className={cn('sticky top-0 z-10 font-medium', colors.surface, colors.textMuted)}>
              Description
            </th>
            <GeneralListSortableTh sortKey="updatedAt" className="whitespace-nowrap">
              Updated
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="createdAt" className="whitespace-nowrap">
              Created
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const pub = getPublishedVersion(row);
            return (
              <tr
                key={row.id}
                className={cn(
                  'border-b transition-colors last:border-b-0',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
              >
                {canFill ? (
                  <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-nowrap items-center gap-0.5">
                      {row.isActive ? (
                        <Link
                          to={`/e-forms/${row.id}/fill`}
                          className={cn(
                            'rounded-lg p-1 transition-colors',
                            colors.primaryFg,
                            colors.interactiveHover,
                          )}
                          title="Fill form"
                          aria-label="Fill form"
                        >
                          <DocumentTextIcon className="h-5 w-5" />
                        </Link>
                      ) : (
                        <span
                          className="rounded-lg p-1 text-slate-300 dark:text-slate-600 cursor-not-allowed inline-flex"
                          title="Form is disabled"
                          aria-label="Form is disabled"
                        >
                          <DocumentTextIcon className="h-5 w-5" />
                        </span>
                      )}
                      <Link
                        to={`/e-forms/${row.id}/submissions`}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                        title="Submissions"
                        aria-label="Submissions"
                      >
                        <TableIcon className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/e-forms/${row.id}/reports`}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                        title="Reports"
                        aria-label="Reports"
                      >
                        <ChartTrendingUpIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </td>
                ) : null}
                <td className="whitespace-nowrap font-medium">{row.name}</td>
                <td className="whitespace-nowrap">
                  {pub ? (
                    <Badge variant="success">v{pub.version}</Badge>
                  ) : (
                    <Badge variant="secondary">Draft only</Badge>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <Badge variant={row.isActive ? 'success' : 'danger'}>
                    {row.isActive ? 'Yes' : 'No'}
                  </Badge>
                </td>
                <td className="whitespace-nowrap tabular-nums">{row._count?.submissions ?? 0}</td>
                <td className="max-w-[18rem]">
                  <span
                    className={cn('block truncate', colors.textMuted)}
                    title={row.description ?? undefined}
                  >
                    {row.description?.trim() ? row.description : '—'}
                  </span>
                </td>
                <td className="whitespace-nowrap">
                  {row.updatedAt ? formatDate(String(row.updatedAt)) : '—'}
                </td>
                <td className="whitespace-nowrap">
                  {row.createdAt ? formatDate(String(row.createdAt)) : '—'}
                </td>
                <td aria-hidden />
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

function SidebarVisibilityCell({
  row,
  pathLabels,
}: {
  row: EFormDefinition;
  pathLabels: Record<string, string>;
}) {
  const { colors } = useTheme();
  const published = getPublishedVersion(row);
  const draft = getDraftVersion(row);
  const schemaJson = published?.schemaJson ?? draft?.schemaJson;
  const entries = describeSidebarPlacements(sidebarPlacementsFromSchema(schemaJson), pathLabels);

  if (!row.isActive) {
    return <span className={cn('text-xs', colors.textMuted)}>Not shown (disabled)</span>;
  }

  if (!published) {
    return (
      <div className="space-y-1">
        {entries.map((entry) => (
          <SidebarVisibilityBadge key={`${entry.kind}-${entry.label}`} entry={entry} />
        ))}
        <span className={cn('block text-[10px] uppercase tracking-wide', colors.textMuted)}>
          Draft only
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {entries.map((entry) => (
        <SidebarVisibilityBadge key={`${entry.kind}-${entry.label}`} entry={entry} />
      ))}
    </div>
  );
}

function SidebarVisibilityBadge({
  entry,
}: {
  entry: ReturnType<typeof describeSidebarPlacements>[number];
}) {
  if (entry.kind === 'catalog') {
    return <Badge variant="secondary">E-Forms</Badge>;
  }
  if (entry.kind === 'shortcut') {
    return (
      <Badge variant="info" title={entry.label}>
        Shortcut · {entry.label}
      </Badge>
    );
  }
  return <Badge variant="secondary">Hidden</Badge>;
}

/** Admin list of all form definitions — open builder. */
export const BuilderList: React.FC = () => {
  const navigate = useNavigate();
  const { can, user, permissionsLoaded } = useAuth();
  const { colors } = useTheme();
  const { entitlements } = useModuleEntitlements();
  const vesselAccountId = useAccountVesselScope();
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const canEdit = can('e_form_definitions', 'edit');
  const canDelete = can('e_form_definitions', 'delete');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<EnabledTab>('all');
  const [creating, setCreating] = useState(false);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'name', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc',
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction]);

  const listOptions = useMemo((): ListQueryOptions => {
    const filters: Record<string, string[]> = {};
    if (tab === 'active') filters.isActive = ['Yes'];
    if (tab === 'inactive') filters.isActive = ['No'];
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: Object.keys(filters).length ? { filters } : undefined,
    };
  }, [tab, sortConfig.key, sortConfig.direction]);

  const { data, total, isLoading, mutate } = useEFormDefinitions(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const canView = useMemo(() => {
    if (!(permissionsLoaded || user?.role === 'ADMIN')) return undefined;
    return (moduleKey: string) => can(moduleKey, 'view');
  }, [can, permissionsLoaded, user?.role]);

  const sidebarPathLabels = useMemo(
    () => collectSidebarPathLabels(getSidebarItems(canView, vesselAccountId, entitlements)),
    [canView, vesselAccountId, entitlements],
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || tab !== 'all');
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setTab('all');
  };

  const handleNew = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const stamp = Date.now().toString(36).slice(-6);
      const created = await createEFormDefinition({
        code: `FORM_${stamp}`.toUpperCase(),
        name: 'New form',
        isActive: true,
      });
      navigate(`/e-forms/builder/${created.id}?section=design`);
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } }; message?: string };
      showError(ex?.response?.data?.message || ex?.message || 'Could not create form');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, row: EFormDefinition) => {
    e.stopPropagation();
    if (!canDeleteEFormDefinition(row)) return;
    const ok = await confirm({
      title: 'Delete form',
      message: `Delete “${row.name}”? Submitted responses block deletion.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteEFormDefinition(row.id);
      await mutate();
      showSuccess('Form deleted');
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } }; message?: string };
      showError(ex?.response?.data?.message || ex?.message || 'Could not delete form.');
    }
  };

  const handleClone = async (e: React.MouseEvent, row: EFormDefinition) => {
    e.stopPropagation();
    try {
      const created = await cloneEFormDefinition(row.id);
      showSuccess('Form cloned');
      navigate(`/e-forms/builder/${created.id}?section=design`);
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } }; message?: string };
      showError(ex?.response?.data?.message || ex?.message || 'Clone failed');
    }
  };

  return (
    <GeneralList
      tabs={enabledTabs.map(({ id, label, icon }) => ({ id, label, icon }))}
      activeTab={tab}
      onTabChange={setTab}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search forms by name…',
      }}
      actions={
        canEdit ? (
          <button
            type="button"
            disabled={creating}
            onClick={handleNew}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-white shadow-sm disabled:opacity-60',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            {creating ? 'Creating…' : 'New form'}
          </button>
        ) : null
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading forms…"
      isEmpty={!isLoading && data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? 'No forms match your search' : 'No forms yet',
        hint: hasFilters
          ? 'Try adjusting filters or clearing the search.'
          : 'Create a form to get started.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : canEdit ? (
          <button
            type="button"
            disabled={creating}
            onClick={handleNew}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            {creating ? 'Creating…' : 'New form'}
          </button>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap">
              Name
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
              Published
            </th>
            <GeneralListSortableTh sortKey="isActive" className="whitespace-nowrap">
              Enabled
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 font-medium min-w-[12rem]', colors.surface, colors.textMuted)}>
              Sidebar visibility
            </th>
            <th className={cn('sticky top-0 z-10 font-medium', colors.surface, colors.textMuted)}>
              Description
            </th>
            <GeneralListSortableTh sortKey="updatedAt" className="whitespace-nowrap">
              Updated
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="createdAt" className="whitespace-nowrap">
              Created
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const pub = getPublishedVersion(row);
            const deletable = canDeleteEFormDefinition(row);
            const deleteTitle = deletable
              ? 'Delete form'
              : pub
                ? 'Only draft forms can be deleted'
                : 'Cannot delete a form that has submissions';
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/e-forms/builder/${row.id}`)}
                className={cn(
                  'cursor-pointer border-b transition-colors last:border-b-0',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
              >
                <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-nowrap items-center gap-0.5">
                    {canEdit ? (
                      <Link
                        to={`/e-forms/builder/${row.id}?section=design`}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          colors.primaryFg,
                          colors.interactiveHover,
                        )}
                        title="Edit"
                        aria-label="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    ) : null}
                    {canEdit ? (
                      <button
                        type="button"
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                        title="Clone form"
                        aria-label="Clone form"
                        onClick={(e) => handleClone(e, row)}
                      >
                        <CopyIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        disabled={!deletable}
                        className={
                          deletable
                            ? 'rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40'
                            : 'rounded-lg p-1 cursor-not-allowed text-slate-300 dark:text-slate-600'
                        }
                        title={deleteTitle}
                        aria-label={deleteTitle}
                        onClick={(e) => handleDelete(e, row)}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="whitespace-nowrap">
                  <Link
                    to={`/e-forms/builder/${row.id}`}
                    className={cn('font-medium hover:underline', colors.primaryFg)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.name}
                  </Link>
                </td>
                <td className="whitespace-nowrap">
                  {pub ? (
                    <Badge variant="success">v{pub.version}</Badge>
                  ) : (
                    <Badge variant="secondary">Draft only</Badge>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <Badge variant={row.isActive ? 'success' : 'secondary'}>
                    {row.isActive ? 'Enabled' : 'Disabled'}
                  </Badge>
                </td>
                <td className="min-w-[12rem] max-w-[20rem]" onClick={(e) => e.stopPropagation()}>
                  <SidebarVisibilityCell row={row} pathLabels={sidebarPathLabels} />
                </td>
                <td className="max-w-[18rem]">
                  <span
                    className={cn('block truncate', colors.textMuted)}
                    title={row.description ?? undefined}
                  >
                    {row.description?.trim() ? row.description : '—'}
                  </span>
                </td>
                <td className="whitespace-nowrap">
                  {row.updatedAt ? formatDate(String(row.updatedAt)) : '—'}
                </td>
                <td className="whitespace-nowrap">
                  {row.createdAt ? formatDate(String(row.createdAt)) : '—'}
                </td>
                <td aria-hidden />
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};
